
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiStar, FiSearch, FiFilter, FiUser, 
  FiCalendar, FiFlag, FiTrash2, FiPackage 
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import api from '../../api/api.js';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    rating: 'all',
    status: 'all',
    dateRange: { start: '', end: '' },
    verified: 'all'
  });

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setIsLoading(true);
        const response = await api.getProductReviews();
        setReviews(response);
        setFilteredReviews(response);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        toast.error('Failed to load reviews');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Filter reviews based on search term and filters
  useEffect(() => {
    let result = [...reviews];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(review => 
        review.customer_name.toLowerCase().includes(searchLower) ||
        review.description.toLowerCase().includes(searchLower) ||
        review.product_details.name.toLowerCase().includes(searchLower) ||
        review.product_details.brand.toLowerCase().includes(searchLower)
      );
    }

    if (filters.rating !== 'all') {
      result = result.filter(review => review.rating === parseInt(filters.rating));
    }

    setFilteredReviews(result);
  }, [reviews, searchTerm, filters]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Helmet>
        <title>Reviews | Admin Dashboard</title>
      </Helmet>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Product Reviews</h1>
        <div className="relative">
          <input
            type="text"
            placeholder="Search reviews..."
            className="pl-10 pr-4 py-2 border rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FiSearch className="absolute left-3 top-3 text-gray-400" />
        </div>
      </div>

      {/* Reviews Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReviews.map((review) => (
          <motion.div
            key={review.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200"
          >
            {/* Product Header */}
            <div className="p-4 border-b">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900 line-clamp-1">
                  {review.product_details.name}
                </h3>
                <div className="flex">
                  {[...Array(5)].map((_, index) => (
                    <FiStar
                      key={index}
                      className={`w-4 h-4 ${
                        index < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                <span className="capitalize">{review.product_details.category}</span>
                {" • "}
                <span>{review.product_details.brand}</span>
              </div>
            </div>

            {/* Review Content */}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FiUser className="text-gray-400 w-4 h-4" />
                <span className="font-medium text-sm">{review.customer_name}</span>
              </div>
              <p className="text-gray-600 text-sm line-clamp-3">{review.description}</p>
            </div>

            {/* Review Footer */}
            <div className="px-4 py-3 bg-gray-50 text-xs text-gray-500 rounded-b-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <FiCalendar className="w-3 h-3" />
                  <span>
                    {new Date(review.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <FiPackage className="w-3 h-3" />
                  <span>ID: {review.product_details.id}</span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredReviews.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          No reviews found matching your criteria
        </div>
      )}
    </div>
  );
};

export default Reviews;






